/**
 * IdleTimeoutManager — admin panelinden yönetilen hareketsizlik (idle) çıkışı.
 *
 * Davranış:
 *  - Kullanıcı `idle_warn_minutes` boyunca hareketsiz olursa uyarı modalı çıkar.
 *  - Modal'da `idle_logout_seconds_after_warn` kadar geri sayım çalışır.
 *  - "Devam Et" → timer reset; "Çıkış Yap" veya geri sayım biterse → logout.
 *  - `idle_enabled=false` ise pasif (hiç çalışmaz).
 *  - Aktivite event'leri: mousemove, keydown, scroll, click, touchstart, visibilitychange.
 *
 * Bu komponent Dashboard içinde mount edilir (sadece login olmuş kullanıcılar için).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "click", "touchstart"];

function formatSeconds(s) {
  const total = Math.max(0, Math.floor(s));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

export default function IdleTimeoutManager() {
  const { isLoggedIn, idleSettings, refreshNow, logout } = useAuth();

  const enabled = Boolean(idleSettings?.idle_enabled ?? true) && isLoggedIn;
  const warnMs = Math.max(60_000, (idleSettings?.idle_warn_minutes ?? 30) * 60 * 1000);
  const countdownStartSeconds = Math.max(30, idleSettings?.idle_logout_seconds_after_warn ?? 120);

  const [warnOpen, setWarnOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(countdownStartSeconds);

  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const stopAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    if (!enabled) return;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Modal'ı aç ve geri sayımı başlat
      setSecondsLeft(countdownStartSeconds);
      setWarnOpen(true);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      const startEpoch = Date.now();
      countdownTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startEpoch) / 1000;
        const remaining = Math.max(0, countdownStartSeconds - elapsed);
        setSecondsLeft(remaining);
        if (remaining <= 0) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          // Otomatik çıkış
          (async () => {
            try { await logout(); } catch (_) { /* ignore */ }
          })();
          setWarnOpen(false);
        }
      }, 250);
    }, warnMs);
  }, [enabled, warnMs, countdownStartSeconds, logout]);

  const onActivity = useCallback(() => {
    if (!enabled) return;
    if (warnOpen) return; // modal açıkken aktivite reset yapmasın — kullanıcı tıkıyla devam etmeli
    lastActivityRef.current = Date.now();
    startIdleTimer();
  }, [enabled, warnOpen, startIdleTimer]);

  useEffect(() => {
    if (!enabled) {
      stopAllTimers();
      setWarnOpen(false);
      return undefined;
    }
    startIdleTimer();
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", onActivity);
    return () => {
      stopAllTimers();
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, [enabled, onActivity, startIdleTimer, stopAllTimers]);

  const handleContinue = useCallback(async () => {
    setWarnOpen(false);
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    lastActivityRef.current = Date.now();
    // Token tazeleme: arka planda refresh dene (zorunlu değil)
    try { await refreshNow(); } catch (_) { /* ignore */ }
    startIdleTimer();
  }, [refreshNow, startIdleTimer]);

  const handleLogoutNow = useCallback(async () => {
    setWarnOpen(false);
    stopAllTimers();
    try { await logout(); } catch (_) { /* ignore */ }
  }, [logout, stopAllTimers]);

  // Geri sayım yüzdesi (görsel ilerleme çubuğu için)
  const progressPct = useMemo(() => {
    if (!countdownStartSeconds) return 0;
    return Math.min(100, Math.max(0, ((countdownStartSeconds - secondsLeft) / countdownStartSeconds) * 100));
  }, [secondsLeft, countdownStartSeconds]);

  if (!enabled) return null;

  return (
    <Dialog open={warnOpen} onOpenChange={(o) => { if (!o) handleContinue(); }}>
      <DialogContent className="sm:max-w-md" data-testid="idle-warn-dialog">
        <DialogHeader>
          <DialogTitle>Hareketsizlik Uyarısı</DialogTitle>
          <DialogDescription className="space-y-2 text-left">
            <p>
              Bir süredir herhangi bir aktivite algılanmadı. Güvenlik için oturumunuz
              <strong> {formatSeconds(secondsLeft)} </strong> sonra otomatik olarak sonlandırılacak.
            </p>
            <p className="text-sm text-muted-foreground">
              Devam etmek istiyorsanız aşağıdaki <strong>Devam Et</strong> butonuna tıklayın.
            </p>
          </DialogDescription>
        </DialogHeader>
        <div className="w-full bg-gray-100 rounded h-2 overflow-hidden" aria-hidden>
          <div
            className="h-full bg-amber-500 transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
          <Button variant="outline" onClick={handleLogoutNow} data-testid="idle-logout-now">
            Çıkış Yap
          </Button>
          <Button onClick={handleContinue} data-testid="idle-continue">
            Devam Et
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
